import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/layout/Layout';
import { useParams, useNavigate } from 'react-router-dom';
import { VisitAttachmentDialog, visitStatusColor } from '../../components/it-visits';
import { getITVisit, updateITVisitStatus, addVisitAsset, updateVisitAsset, removeVisitAsset, getITAssets, addVisitAttachment, deleteVisitAttachment, downloadVisitAttachment, generateQuoteFromVisit, saveVisitCompliance } from '../../lib/api';
import { IT_CYBERSECURITY_CONTROLS, COMPLIANCE_STATUSES, REGULATION_REFERENCE, mergeChecklistWithControls, computeComplianceSummary } from '../../lib/itComplianceControls';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  CalendarCheck, ArrowLeft, Desktop, Plus, Trash, Clock, MapPin,
  User, PencilSimple, FileText, Camera, CheckCircle, Warning,
  CurrencyDollar, Paperclip, ShieldCheck, Printer
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { getITErrorMessage } from '../../lib/itErrors';
import { assetDisplayName } from '../../components/it-assets';

const EMPTY_VISIT_ASSET = {
  asset_id: '', reported_problem: '', diagnosis: '', work_performed: '', recommendation: '',
  status_before: '', status_after: '', observations: '', requires_followup: false,
  time_spent_min: 0, cost: 0,
};

const EMPTY_QUOTE_FORM = {
  items: [{ description: '', quantity: 1, unit_price: 0 }],
  notes: '',
  validity_days: 30,
};

export default function ServiceVisitDetailPage() {
  const { visitId } = useParams();
  const navigate = useNavigate();
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [editingVA, setEditingVA] = useState(null);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [assetForm, setAssetForm] = useState(EMPTY_VISIT_ASSET);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [quoteForm, setQuoteForm] = useState(EMPTY_QUOTE_FORM);
  const [complianceChecklist, setComplianceChecklist] = useState([]);
  const [complianceDirty, setComplianceDirty] = useState(false);
  const [savingCompliance, setSavingCompliance] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadVisit(); }, [visitId]);

  const loadVisit = async () => {
    try {
      setErrorMessage('');
      const res = await getITVisit(visitId);
      setVisit(res.data);
      const incoming = res.data?.cybersecurity_checklist;
      const merged = mergeChecklistWithControls(incoming);
      setComplianceChecklist(merged);
      setComplianceDirty(false);
    } catch (err) {
      const message = getITErrorMessage(err, 'Error loading visit');
      setVisit(null);
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const updateComplianceItem = (controlId, field, value) => {
    setComplianceChecklist(prev => prev.map(item =>
      item.control_id === controlId ? { ...item, [field]: value } : item
    ));
    setComplianceDirty(true);
  };

  const handleSaveCompliance = async () => {
    try {
      setSavingCompliance(true);
      await saveVisitCompliance(visitId, { checklist: complianceChecklist });
      toast.success('Checklist de ciberseguridad guardado');
      setComplianceDirty(false);
      loadVisit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando checklist');
    } finally {
      setSavingCompliance(false);
    }
  };

  const openPrintReport = () => {
    window.open(`/it/visits/${visitId}/report`, '_blank', 'noopener,noreferrer');
  };

  const loadAvailableAssets = async () => {
    try {
      const params = {};
      if (visit?.client_id) params.client_id = visit.client_id;
      const res = await getITAssets(params);
      setAvailableAssets(Array.isArray(res.data) ? res.data : (res.data?.items || []));
    } catch (err) {
      setAvailableAssets([]);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await updateITVisitStatus(visitId, { status: newStatus });
      toast.success(`Estado actualizado: ${newStatus}`);
      loadVisit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleAddAsset = () => {
    loadAvailableAssets();
    setEditingVA(null);
    setAssetForm(EMPTY_VISIT_ASSET);
    setShowAssetModal(true);
  };

  const handleEditAsset = (va) => {
    loadAvailableAssets();
    setEditingVA(va);
    setAssetForm({
      asset_id: va.asset_id,
      reported_problem: va.reported_problem || '',
      diagnosis: va.diagnosis || '',
      work_performed: va.work_performed || '',
      recommendation: va.recommendation || '',
      status_before: va.status_before || '',
      status_after: va.status_after || '',
      observations: va.observations || '',
      requires_followup: Boolean(va.requires_followup),
      time_spent_min: va.time_spent_min || 0,
      cost: va.cost || 0,
    });
    setShowAssetModal(true);
  };

  const handleSubmitAsset = async (e) => {
    e.preventDefault();
    if (!assetForm.asset_id) {
      toast.error('Selecciona un activo');
      return;
    }
    try {
      if (editingVA) {
        const { asset_id, ...changes } = assetForm;
        await updateVisitAsset(visitId, editingVA._id, changes);
        toast.success('Registro de activo actualizado');
      } else {
        await addVisitAsset(visitId, assetForm);
        toast.success('Activo agregado a la visita');
      }
      setShowAssetModal(false);
      loadVisit();
    } catch (err) {
      toast.error(getITErrorMessage(err, 'Error al guardar el activo de la visita'));
    }
  };

  const handleRemoveAsset = async (vaId) => {
    if (!window.confirm('¿Remover este activo de la visita?')) return;
    try {
      await removeVisitAsset(visitId, vaId);
      toast.success('Activo removido');
      loadVisit();
    } catch (err) {
      toast.error('Error removing asset');
    }
  };

  const handleAddAttachment = async (formData) => {
    setUploadingAttachment(true);
    try {
      await addVisitAttachment(visitId, formData);
      toast.success('Adjunto agregado');
      setShowAttachmentModal(false);
      loadVisit();
    } catch (err) {
      toast.error(getITErrorMessage(err, 'No se pudo subir el adjunto'));
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleDownloadAttachment = async (att) => {
    try {
      const res = await downloadVisitAttachment(visitId, att._id);
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = att.filename || att.name || 'adjunto';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getITErrorMessage(err, 'No se pudo descargar el adjunto'));
    }
  };

  const handleDeleteAttachment = async (attId) => {
    try {
      await deleteVisitAttachment(visitId, attId);
      toast.success('Adjunto eliminado');
      loadVisit();
    } catch (err) {
      toast.error('Error');
    }
  };

  const handleGenerateQuote = async (e) => {
    e.preventDefault();
    try {
      const res = await generateQuoteFromVisit(visitId, {
        items: quoteForm.items.filter((item) => item.description.trim()),
        notes: quoteForm.notes.trim() || null,
        validity_days: quoteForm.validity_days,
      });
      toast.success(`Cotización ${res.data.quotation_number} creada`, {
        action: { label: 'Ver cotizaciones', onClick: () => navigate('/quotations') },
      });
      setShowQuoteModal(false);
      loadVisit();
    } catch (err) {
      toast.error(getITErrorMessage(err, 'No se pudo generar la cotización'));
    }
  };

  const addQuoteItem = () => {
    setQuoteForm(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, unit_price: 0 }]
    }));
  };

  const removeQuoteItem = (idx) => {
    setQuoteForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== idx)
    }));
  };

  const updateQuoteItem = (idx, field, value) => {
    setQuoteForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === idx ? { ...item, [field]: value } : item)
    }));
  };


  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E2B05E]"></div>
        </div>
      </Layout>
    );
  }

  if (errorMessage) {
    return (
      <Layout>
        <div className="mx-auto max-w-2xl rounded-2xl border border-[#2A3448] bg-[#151B26] p-6 text-center">
          <h2 className="text-xl font-semibold text-white">No se pudo cargar la visita</h2>
          <p className="mt-2 text-sm text-gray-400">{errorMessage}</p>
          <div className="mt-5 flex items-center justify-center gap-3">
            <Button onClick={loadVisit} className="bg-[#E2B05E] hover:bg-[#c9993f] text-black">Reintentar</Button>
            <Button variant="outline" onClick={() => navigate('/it/visits')} className="border-gray-700 text-gray-200 hover:bg-[#1E2636]">Volver a visitas</Button>
          </div>
        </div>
      </Layout>
    );
  }

  if (!visit) return null;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/it/visits')} className="text-gray-400">
            <ArrowLeft size={20} />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <CalendarCheck className="text-[#E2B05E]" size={24} />
              {visit.visit_number}
            </h1>
            <div className="flex items-center gap-3 mt-1 text-sm text-gray-400">
              <span>{visit.client_name}</span>
              {visit.location_name && <span>· {visit.location_name}</span>}
              <span>· <span className="capitalize">{(visit.tipo || '').replace(/_/g, ' ')}</span></span>
            </div>
          </div>
          <Badge className={`${visitStatusColor(visit.status)} text-sm px-3 py-1`}>
            {(visit.status || '').replace(/_/g, ' ')}
          </Badge>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-2">
          {visit.status === 'programada' && (
            <>
              <Button size="sm" onClick={() => handleStatusChange('en_camino')} className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30">En Camino</Button>
              <Button size="sm" onClick={() => handleStatusChange('cancelada')} variant="outline" className="border-red-500/30 text-red-400">Cancelar</Button>
            </>
          )}
          {visit.status === 'en_camino' && (
            <Button size="sm" onClick={() => handleStatusChange('en_sitio')} className="bg-purple-500/20 text-purple-400 hover:bg-purple-500/30">
              <MapPin size={16} className="mr-1" /> Check-In
            </Button>
          )}
          {visit.status === 'en_sitio' && (
            <>
              <Button size="sm" onClick={() => handleStatusChange('completada')} className="bg-green-500/20 text-green-400 hover:bg-green-500/30">
                <CheckCircle size={16} className="mr-1" /> Completar
              </Button>
              <Button size="sm" onClick={() => handleStatusChange('pausada')} variant="outline" className="border-orange-500/30 text-orange-400">Pausar</Button>
            </>
          )}
          {visit.status === 'pausada' && (
            <Button size="sm" onClick={() => handleStatusChange('en_sitio')} className="bg-purple-500/20 text-purple-400">Reanudar</Button>
          )}
          <Button size="sm" onClick={() => { setQuoteForm(EMPTY_QUOTE_FORM); setShowQuoteModal(true); }} variant="outline" className="border-[#E2B05E]/50 text-[#E2B05E]">
            <CurrencyDollar size={16} className="mr-1" /> Generar Cotización
          </Button>
          <Button size="sm" onClick={openPrintReport} variant="outline" className="border-[#3AB8D6]/50 text-[#3AB8D6]">
            <Printer size={16} className="mr-1" /> Imprimir Reporte
          </Button>
        </div>

        <Tabs defaultValue="info" className="w-full">
          <TabsList className="bg-[#12141a] border border-gray-700">
            <TabsTrigger value="info">Información</TabsTrigger>
            <TabsTrigger value="assets">Activos ({(visit.assets || []).length})</TabsTrigger>
            <TabsTrigger value="attachments">Adjuntos ({(visit.attachments || []).length})</TabsTrigger>
            <TabsTrigger value="cybersecurity">Ciberseguridad</TabsTrigger>
          </TabsList>

          {/* Info Tab */}
          <TabsContent value="info" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-5">
                <h3 className="text-[#E2B05E] font-semibold mb-3">Detalles de la Visita</h3>
                <div className="space-y-2 text-sm">
                  {[
                    ['Tipo', (visit.tipo || '').replace(/_/g, ' ')],
                    ['Prioridad', visit.priority],
                    ['Programada', (visit.scheduled_for || '').replace('T', ' ').slice(0, 16)],
                    ['Duración Estimada', visit.duracion_estimada_min ? `${visit.duracion_estimada_min} min` : '-'],
                    ['Inicio', visit.started_at ? new Date(visit.started_at).toLocaleString() : '-'],
                    ['Fin', visit.completed_at ? new Date(visit.completed_at).toLocaleString() : '-'],
                    ['Recibe', visit.recibe_nombre]
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} className="flex justify-between">
                      <span className="text-gray-400">{label}</span>
                      <span className="text-white capitalize">{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-5">
                <h3 className="text-[#E2B05E] font-semibold mb-3">Técnicos Asignados</h3>
                {(visit.technicians || []).length === 0 ? (
                  <p className="text-gray-500 text-sm">Sin técnicos asignados</p>
                ) : (
                  <div className="space-y-2">
                    {visit.technicians.map((tech, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-[#12141a] rounded-lg p-2">
                        <User size={16} className="text-[#3AB8D6]" />
                        <span className="text-white text-sm">{tech.employee_name || tech.employee_id}</span>
                        <Badge className="text-xs bg-[#3AB8D6]/20 text-[#3AB8D6]">{tech.role}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {visit.description && (
              <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-5">
                <h3 className="text-[#E2B05E] font-semibold mb-2">Descripción</h3>
                <p className="text-gray-300 text-sm whitespace-pre-wrap">{visit.description}</p>
              </div>
            )}

            {visit.checklist?.length > 0 && (
              <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-5">
                <h3 className="text-[#E2B05E] font-semibold mb-3">Checklist</h3>
                <div className="space-y-2">
                  {visit.checklist.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className={`w-5 h-5 rounded border ${item.completed ? 'bg-green-500/20 border-green-500 text-green-400' : 'border-gray-600'} flex items-center justify-center text-xs`}>
                        {item.completed && '✓'}
                      </div>
                      <span className={`text-sm ${item.completed ? 'text-gray-300' : 'text-gray-500'}`}>{item.item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Assets Tab */}
          <TabsContent value="assets" className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-semibold">Activos en esta Visita</h3>
              <Button size="sm" onClick={handleAddAsset} className="bg-[#3AB8D6] text-black">
                <Plus size={16} className="mr-1" /> Agregar Activo
              </Button>
            </div>

            {(visit.assets || []).length === 0 ? (
              <div className="bg-[#1a1d23] border border-gray-800 rounded-lg text-center py-8 text-gray-500">
                <Desktop size={40} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No hay activos asociados a esta visita</p>
              </div>
            ) : (
              <div className="space-y-3">
                {visit.assets.map((va) => (
                  <div key={va._id} className="bg-[#1a1d23] border border-gray-800 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="text-white font-medium text-sm flex items-center gap-2">
                          <Desktop size={16} className="text-[#3AB8D6]" />
                          {va.asset_name || 'Activo'}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {va.asset_serial && `S/N: ${va.asset_serial}`}
                          {va.asset_tag && ` · Tag: ${va.asset_tag}`}
                          {va.asset_category && ` · ${va.asset_category}`}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleEditAsset(va)}>
                          <PencilSimple size={14} className="text-[#E2B05E]" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleRemoveAsset(va._id)}>
                          <Trash size={14} className="text-red-400" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                      {va.reported_problem && (
                        <div className="bg-[#12141a] rounded p-2">
                          <span className="text-gray-500">Problema:</span>
                          <p className="text-gray-300">{va.reported_problem}</p>
                        </div>
                      )}
                      {va.diagnosis && (
                        <div className="bg-[#12141a] rounded p-2">
                          <span className="text-gray-500">Diagnóstico:</span>
                          <p className="text-[#3AB8D6]">{va.diagnosis}</p>
                        </div>
                      )}
                      {va.work_performed && (
                        <div className="bg-[#12141a] rounded p-2">
                          <span className="text-gray-500">Acciones:</span>
                          <p className="text-green-400">{va.work_performed}</p>
                        </div>
                      )}
                      {va.recommendation && (
                        <div className="bg-[#12141a] rounded p-2">
                          <span className="text-gray-500">Recomendación:</span>
                          <p className="text-yellow-400">{va.recommendation}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      {va.time_spent_min > 0 && <span>⏱ {va.time_spent_min} min</span>}
                      {va.cost > 0 && <span>💰 ${va.cost}</span>}
                      {va.requires_followup && <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">Seguimiento</Badge>}
                      {va.status_after && <Badge className="bg-blue-500/20 text-blue-400 text-xs">{va.status_after}</Badge>}
                      {va.observations && <span className="text-gray-400">{va.observations}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Attachments Tab */}
          <TabsContent value="attachments" className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-semibold">Adjuntos y Evidencia</h3>
              <Button size="sm" onClick={() => setShowAttachmentModal(true)} className="bg-[#3AB8D6] text-black">
                <Plus size={16} className="mr-1" /> Agregar Adjunto
              </Button>
            </div>

            {(visit.attachments || []).length === 0 ? (
              <div className="bg-[#1a1d23] border border-gray-800 rounded-lg text-center py-8 text-gray-500">
                <Camera size={40} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No hay adjuntos</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {visit.attachments.map((att) => (
                  <div key={att._id} className="bg-[#1a1d23] border border-gray-800 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <Badge className="bg-[#3AB8D6]/20 text-[#3AB8D6] text-xs">{att.kind || 'archivo'}</Badge>
                      <div className="flex items-center gap-1">
                        {att.phase && <Badge className="text-xs bg-gray-600/20 text-gray-400">{att.phase}</Badge>}
                        <Button size="sm" variant="ghost" aria-label="Eliminar adjunto" onClick={() => handleDeleteAttachment(att._id)}>
                          <Trash size={12} className="text-red-400" />
                        </Button>
                      </div>
                    </div>
                    <div className="text-sm text-white">{att.name || 'Sin nombre'}</div>
                    {att.description && <p className="text-xs text-gray-400 mt-1">{att.description}</p>}
                    {att.file_id && (
                      <button
                        type="button"
                        onClick={() => handleDownloadAttachment(att)}
                        className="text-xs text-[#3AB8D6] hover:underline mt-1 block truncate text-left"
                      >
                        Descargar {att.filename || 'archivo'}{att.size ? ` (${Math.max(1, Math.round(att.size / 1024))} KB)` : ''}
                      </button>
                    )}
                    {att.url && (
                      <a href={att.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#3AB8D6] hover:underline mt-1 block truncate">
                        {att.url}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Cybersecurity Compliance Tab */}
          <TabsContent value="cybersecurity" className="space-y-4">
            <div className="bg-[#1a1d23] border border-gray-800 rounded-lg p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <ShieldCheck className="text-[#3AB8D6]" size={20} />
                    Checklist de Ciberseguridad
                  </h3>
                  <p className="text-xs text-gray-400 mt-1">
                    {REGULATION_REFERENCE.norma} — {REGULATION_REFERENCE.emisor}
                  </p>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    Plan de acción máximo {REGULATION_REFERENCE.plazo_plan_accion_dias} días calendario desde el envío del informe de autogestión.
                  </p>
                </div>
                {(() => {
                  const s = computeComplianceSummary(complianceChecklist);
                  return (
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="text-xs text-gray-500">Cumplimiento</div>
                        <div className={`text-2xl font-bold ${s.percent >= 80 ? 'text-green-400' : s.percent >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {s.percent}%
                        </div>
                      </div>
                      <div className="text-xs text-gray-400 leading-5">
                        <div><span className="text-green-400 font-semibold">{s.counts.cumple}</span> cumple</div>
                        <div><span className="text-yellow-400 font-semibold">{s.counts.parcial}</span> parcial</div>
                        <div><span className="text-red-400 font-semibold">{s.counts.no_cumple}</span> no cumple</div>
                        <div><span className="text-gray-400">{s.counts.no_aplica}</span> N/A · <span className="text-gray-500">{s.counts.sin_evaluar}</span> sin evaluar</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="space-y-3">
              {IT_CYBERSECURITY_CONTROLS.map((control) => {
                const item = complianceChecklist.find(c => c.control_id === control.id) || {};
                const statusMeta = COMPLIANCE_STATUSES.find(s => s.id === item.estado);
                const requiresPlan = item.estado === 'no_cumple' || item.estado === 'parcial';
                return (
                  <div key={control.id} className="bg-[#1a1d23] border border-gray-800 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[#3AB8D6] font-bold text-sm">#{control.numero}</span>
                          <span className="text-white font-semibold text-sm">{control.titulo}</span>
                          {statusMeta && (
                            <Badge className={`${statusMeta.color} border text-[11px]`}>{statusMeta.label}</Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 leading-5">{control.descripcion}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-gray-300 text-xs">Estado</Label>
                        <Select
                          value={item.estado || ''}
                          onValueChange={(v) => updateComplianceItem(control.id, 'estado', v)}
                        >
                          <SelectTrigger className="bg-[#12141a] border-gray-700 text-white">
                            <SelectValue placeholder="Sin evaluar" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1a1d23] border-gray-700">
                            {COMPLIANCE_STATUSES.map(s => (
                              <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-gray-300 text-xs">Evidencia</Label>
                        <Input
                          value={item.evidencia || ''}
                          onChange={(e) => updateComplianceItem(control.id, 'evidencia', e.target.value)}
                          className="bg-[#12141a] border-gray-700 text-white"
                          placeholder="Sistema, política, URL del documento..."
                        />
                      </div>
                      {requiresPlan && (
                        <>
                          <div className="md:col-span-2">
                            <Label className="text-gray-300 text-xs">Plan de Acción</Label>
                            <Textarea
                              value={item.plan_accion || ''}
                              onChange={(e) => updateComplianceItem(control.id, 'plan_accion', e.target.value)}
                              className="bg-[#12141a] border-gray-700 text-white"
                              rows={2}
                              placeholder="Acciones para remediar el incumplimiento"
                            />
                          </div>
                          <div>
                            <Label className="text-gray-300 text-xs">Fecha Objetivo (máx. 45 días)</Label>
                            <Input
                              type="date"
                              value={item.fecha_objetivo || ''}
                              onChange={(e) => updateComplianceItem(control.id, 'fecha_objetivo', e.target.value)}
                              className="bg-[#12141a] border-gray-700 text-white"
                            />
                          </div>
                        </>
                      )}
                      <div className={requiresPlan ? '' : 'md:col-span-2'}>
                        <Label className="text-gray-300 text-xs">Notas</Label>
                        <Textarea
                          value={item.notas || ''}
                          onChange={(e) => updateComplianceItem(control.id, 'notas', e.target.value)}
                          className="bg-[#12141a] border-gray-700 text-white"
                          rows={1}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end gap-3 sticky bottom-2 bg-[#0f1218]/85 backdrop-blur rounded-lg p-2">
              {complianceDirty && (
                <span className="text-xs text-yellow-400 self-center flex items-center gap-1">
                  <Warning size={14} /> Cambios sin guardar
                </span>
              )}
              <Button
                onClick={handleSaveCompliance}
                disabled={!complianceDirty || savingCompliance}
                className="bg-[#E2B05E] hover:bg-[#c9993f] text-black disabled:opacity-50"
              >
                {savingCompliance ? 'Guardando...' : 'Guardar Checklist'}
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        {/* Add/Edit Asset Modal */}
        <Dialog open={showAssetModal} onOpenChange={setShowAssetModal}>
          <DialogContent className="bg-[#1a1d23] border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#E2B05E]">
                {editingVA ? 'Editar Detalle de Activo' : 'Agregar Activo a Visita'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitAsset} className="space-y-4">
              {!editingVA && (
                <div>
                  <Label className="text-gray-300">Seleccionar Activo *</Label>
                  <Select value={assetForm.asset_id} onValueChange={(v) => setAssetForm(prev => ({ ...prev, asset_id: v }))}>
                    <SelectTrigger className="bg-[#12141a] border-gray-700 text-white">
                      <SelectValue placeholder="Buscar activo..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1d23] border-gray-700 max-h-60">
                      {availableAssets.map(a => (
                        <SelectItem key={a._id} value={a._id}>
                          {assetDisplayName(a)} {a.serial_number ? `(${a.serial_number})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-gray-300">Problema Reportado</Label>
                  <Textarea value={assetForm.reported_problem} onChange={(e) => setAssetForm(prev => ({ ...prev, reported_problem: e.target.value }))} className="bg-[#12141a] border-gray-700 text-white" rows={2} />
                </div>
                <div className="col-span-2">
                  <Label className="text-gray-300">Diagnóstico</Label>
                  <Textarea value={assetForm.diagnosis} onChange={(e) => setAssetForm(prev => ({ ...prev, diagnosis: e.target.value }))} className="bg-[#12141a] border-gray-700 text-white" rows={2} />
                </div>
                <div className="col-span-2">
                  <Label className="text-gray-300">Acciones Realizadas</Label>
                  <Textarea value={assetForm.work_performed} onChange={(e) => setAssetForm(prev => ({ ...prev, work_performed: e.target.value }))} className="bg-[#12141a] border-gray-700 text-white" rows={2} />
                </div>
                <div className="col-span-2">
                  <Label className="text-gray-300">Recomendación</Label>
                  <Textarea value={assetForm.recommendation} onChange={(e) => setAssetForm(prev => ({ ...prev, recommendation: e.target.value }))} className="bg-[#12141a] border-gray-700 text-white" rows={2} />
                </div>
                <div>
                  <Label className="text-gray-300">Estado Final del Activo</Label>
                  <Select value={assetForm.status_after || 'none'} onValueChange={(v) => setAssetForm(prev => ({ ...prev, status_after: v === 'none' ? '' : v }))}>
                    <SelectTrigger className="bg-[#12141a] border-gray-700 text-white">
                      <SelectValue placeholder="Sin cambio" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1d23] border-gray-700">
                      <SelectItem value="none">Sin cambio</SelectItem>
                      <SelectItem value="activo">Activo</SelectItem>
                      <SelectItem value="en_reparacion">En Reparación</SelectItem>
                      <SelectItem value="dado_de_baja">Dado de Baja</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Tiempo Dedicado (min)</Label>
                  <Input type="number" value={assetForm.time_spent_min} onChange={(e) => setAssetForm(prev => ({ ...prev, time_spent_min: parseInt(e.target.value) || 0 }))} className="bg-[#12141a] border-gray-700 text-white" />
                </div>
                <div>
                  <Label className="text-gray-300">Costo Asociado</Label>
                  <Input type="number" step="0.01" value={assetForm.cost} onChange={(e) => setAssetForm(prev => ({ ...prev, cost: parseFloat(e.target.value) || 0 }))} className="bg-[#12141a] border-gray-700 text-white" />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input type="checkbox" checked={assetForm.requires_followup} onChange={(e) => setAssetForm(prev => ({ ...prev, requires_followup: e.target.checked }))} className="rounded" />
                  <Label className="text-gray-300">Requiere Seguimiento</Label>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <Button type="button" variant="outline" onClick={() => setShowAssetModal(false)} className="border-gray-600 text-gray-300">Cancelar</Button>
                <Button type="submit" className="bg-[#E2B05E] hover:bg-[#c9993f] text-black">
                  {editingVA ? 'Actualizar' : 'Agregar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <VisitAttachmentDialog
          open={showAttachmentModal}
          onOpenChange={setShowAttachmentModal}
          onSubmit={handleAddAttachment}
          submitting={uploadingAttachment}
        />

        {/* Generate Quote Modal */}
        <Dialog open={showQuoteModal} onOpenChange={setShowQuoteModal}>
          <DialogContent className="bg-[#1a1d23] border-gray-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#E2B05E]">Generar Cotización</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleGenerateQuote} className="space-y-4">
              <p className="text-sm text-gray-400">
                Se crea una cotización normal (borrador) en <strong className="text-gray-200">Cotizaciones</strong>, con ITBMS 7%. Si no agregas ítems, se genera con los activos de la visita en $0 para que los completes allí.
              </p>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-gray-300">Items</Label>
                  <Button type="button" size="sm" onClick={addQuoteItem} className="bg-[#3AB8D6] text-black text-xs">
                    <Plus size={14} className="mr-1" /> Item
                  </Button>
                </div>
                <div className="space-y-2">
                  {quoteForm.items.map((item, idx) => (
                    <div key={idx} className="flex gap-2 items-start bg-[#12141a] rounded-lg p-2">
                      <Input placeholder="Descripción" value={item.description} onChange={(e) => updateQuoteItem(idx, 'description', e.target.value)} className="bg-[#0d0f14] border-gray-700 text-white flex-1 text-xs" />
                      <Input type="number" placeholder="Cant" value={item.quantity} onChange={(e) => updateQuoteItem(idx, 'quantity', parseFloat(e.target.value) || 1)} className="bg-[#0d0f14] border-gray-700 text-white w-20 text-xs" />
                      <Input type="number" step="0.01" placeholder="Precio" value={item.unit_price} onChange={(e) => updateQuoteItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} className="bg-[#0d0f14] border-gray-700 text-white w-28 text-xs" />
                      {quoteForm.items.length > 1 && (
                        <Button type="button" size="sm" variant="ghost" onClick={() => removeQuoteItem(idx)}>
                          <Trash size={14} className="text-red-400" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <Button type="button" variant="outline" onClick={() => setShowQuoteModal(false)} className="border-gray-600 text-gray-300">Cancelar</Button>
                <Button type="submit" className="bg-[#E2B05E] hover:bg-[#c9993f] text-black">Generar Cotización</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
